import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { useAuthConfig } from "../hooks/useAuthConfig";

export function RegisterPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { registrationOpen } = useAuthConfig();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.auth.register({
        username,
        password,
        displayName: displayName || undefined,
      });
      await qc.invalidateQueries({ queryKey: ["me"] });
      navigate("/boards");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!registrationOpen) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="w-full max-w-sm space-y-4 rounded-lg border border-slate-200 bg-[var(--surface)] p-6 text-center shadow-sm">
          <h1 className="text-xl font-semibold">Registration is closed</h1>
          <p className="text-sm text-slate-600">
            This Kandoo server isn’t accepting new accounts. Ask an administrator
            to create one for you.
          </p>
          <p className="text-sm text-slate-600">
            <Link to="/login" className="text-blue-600">
              Back to log in
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm space-y-4 rounded-lg border border-slate-200 bg-[var(--surface)] p-6 shadow-sm"
      >
        <h1 className="text-xl font-semibold">Create your Kandoo account</h1>
        {error && (
          <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        <label className="block text-sm">
          <span className="text-slate-600">Username</span>
          <input
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
            required
          />
        </label>
        <label className="block text-sm">
          <span className="text-slate-600">Password (min 8 chars)</span>
          <input
            type="password"
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        <label className="block text-sm">
          <span className="text-slate-600">Display name (optional)</span>
          <input
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </label>
        <button
          type="submit"
          disabled={busy}
          className="w-full rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {busy ? "Creating…" : "Register"}
        </button>
        <p className="text-center text-sm text-slate-600">
          Have an account? <Link to="/login" className="text-blue-600">Log in</Link>
        </p>
      </form>
    </div>
  );
}